/**
 * OrganizationGuard - Vérifie que l'organisation est active et validée par Stripe
 * Bloque l'accès à l'order journey si :
 * - L'organisation est désactivée (isActive = false)
 * - Stripe n'est pas configuré ou non validé (stripeStatus !== 'active')
 */

import React from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Lock, CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useOrgaCompletion } from '@/shared/hooks/useOrgaCompletion';
import { useUserAffiliation } from '@/shared/hooks/useUserAffiliation';
import { usePermissionsHook } from '@/shared/hooks/usePermissionsHook';
import { useAuth, OrgaConfigurationProvider } from '@/app/providers';
import SeoTags from '@/components/shared/SeoTags';

interface OrganizationGuardProps {
  children: React.ReactNode;
}

const OrganizationGuard: React.FC<OrganizationGuardProps> = ({ children }) => {
  const { idorga } = useParams<{ idorga: string }>();
  const searchParams = new URLSearchParams(globalThis.location?.search || '');
  const isPersonalizationMode = searchParams.get('edit') === 'personalization';
  const isOrderRoute = globalThis.location?.pathname?.includes('/order');

  const {
    configuration,
    loading,
    error,
    isStripeValid,
    hasShippingMethods,
    hasSenderAddress,
    hasContactConfigured,
    isGeneralReady,
  } = useOrgaCompletion(undefined, { once: true });
  const { affiliations } = useUserAffiliation();
  const { currentUser } = useAuth();
  const { canManageConfiguration } = usePermissionsHook();
  const { t } = useTranslation();

  // Vérifier si l'utilisateur a les droits pour gérer la personnalisation
  const canManagePersonalization = idorga && currentUser
    ? canManageConfiguration(idorga)
    : false;

  // Chargement
  if (loading) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">{t('admin.configuration.organizationGuard.loading.title')}</p>
          <p className="text-gray-400 text-sm mt-2">{t('admin.configuration.organizationGuard.loading.subtitle')}</p>
        </div>
      </div>
    );
  }

  // Erreur ou configuration non trouvée
  if (error || !configuration) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('admin.configuration.organizationGuard.notConfigured.title')}
            </h1>
            <p className="text-gray-600 mb-6">
              {t('admin.configuration.organizationGuard.notConfigured.message', { idorga: idorga || '' })}
            </p>
            <button
              onClick={() => globalThis.location.href = '/'}
              className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {t('admin.configuration.organizationGuard.notConfigured.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier si l'organisation est active (sauf édition personnalisation par un admin)
  if (!configuration.isActive && !(isPersonalizationMode && canManagePersonalization)) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <SeoTags
          title={`${configuration.name || idorga || 'CashLive'}`}
          description={`Portail désactivé`}
          url={typeof window !== 'undefined' ? window.location.href : ''}
          noIndex={true}
        />
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('admin.configuration.organizationGuard.deactivated.title')}
            </h1>
            <p
              className="text-gray-600 mb-2"
              dangerouslySetInnerHTML={{
                __html: t('admin.configuration.organizationGuard.deactivated.message', { name: configuration.name || idorga || '' })
              }}
            />
            <p className="text-gray-500 text-sm mb-6">
              {t('admin.configuration.organizationGuard.deactivated.description')}
            </p>

            {/* Logo de l'organisation si disponible */}
            {configuration.personalization?.logo && (
              <div className="mb-6">
                <img
                  src={configuration.personalization.logo}
                  alt={configuration.name}
                  className="h-16 mx-auto object-contain"
                />
              </div>
            )}

            <button
              onClick={() => globalThis.location.href = '/'}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              {t('admin.configuration.organizationGuard.deactivated.backToHome')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Vérifier si l'utilisateur est admin de cette organisation
  const isAdmin = affiliations?.some(affiliation =>
    affiliation.organizationId === idorga && affiliation.role === 'admin'
  );



  // Si on est en mode personnalisation, vérifier que l'utilisateur a les droits
  if (isPersonalizationMode && !canManagePersonalization) {
    // Retirer le paramètre edit de l'URL et rediriger
    const newUrl = new URL(globalThis.location.href);
    newUrl.searchParams.delete('edit');
    globalThis.location.href = newUrl.toString();
    return null; // Ne rien afficher pendant la redirection
  }

  // Pour les routes d'order, vérifier que Stripe, la livraison, l'adresse expéditeur, les contacts ET les infos générales sont configurés
  // Exception: permettre l'accès en mode personnalisation même si la config n'est pas complète (seulement si l'utilisateur a les droits)
  if (isOrderRoute && !(isPersonalizationMode && canManagePersonalization) && (!isStripeValid || !hasShippingMethods || !hasSenderAddress || !hasContactConfigured || !isGeneralReady)) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <SeoTags
          title={`${configuration.name || idorga || 'CashLive'}`}
          description={`Configuration en cours`}
          url={typeof window !== 'undefined' ? window.location.href : ''}
          noIndex={true}
        />
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-10 h-10 text-orange-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('admin.configuration.organizationGuard.configurationInProgress.title')}
            </h1>
            <p
              className="text-gray-600 mb-2"
              dangerouslySetInnerHTML={{
                __html: t('admin.configuration.organizationGuard.configurationInProgress.message', { name: configuration.name || idorga || '' })
              }}
            />
            <p className="text-gray-500 text-sm mb-4">
              {t('admin.configuration.organizationGuard.configurationInProgress.description')}
            </p>

            {/* Logo de l'organisation si disponible */}
            {configuration.personalization?.logo && (
              <div className="mb-4">
                <img
                  src={configuration.personalization.logo}
                  alt={configuration.name}
                  className="h-16 mx-auto object-contain"
                />
              </div>
            )}

            {/* Détails du statut de configuration */}
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-medium text-orange-900 mb-2">
                {t('admin.configuration.organizationGuard.configurationInProgress.statusTitle')}
              </h3>
              <div className="space-y-1 text-sm">
                {/* Statut Infos Légales */}
                <div className="flex items-center justify-between">
                  <span className="text-orange-900">{t('admin.configuration.organizationGuard.configurationInProgress.legalInfo')}</span>
                  <span className={`font-medium ${isGeneralReady ? 'text-green-600' : 'text-red-600'}`}>
                    {isGeneralReady
                      ? t('admin.configuration.organizationGuard.configurationInProgress.legalInfoConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.legalInfoNone')}
                  </span>
                </div>
                {/* Statut Paiements */}
                <div className="flex items-center justify-between">
                  <span className="text-orange-800">Paiements :</span>
                  <span className={`font-medium ${isStripeValid ? 'text-green-600' : 'text-red-600'}`}>
                    {isStripeValid ? '✓ Configuré' : '✗ Non configuré'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-orange-900">{t('admin.configuration.organizationGuard.configurationInProgress.shippingMethods')}</span>
                  <span className={`font-medium ${hasShippingMethods ? 'text-green-600' : 'text-red-600'}`}>
                    {hasShippingMethods
                      ? t('admin.configuration.organizationGuard.configurationInProgress.shippingMethodsConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.shippingMethodsNone')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-orange-900">Adresse expéditeur</span>
                  <span className={`font-medium ${hasSenderAddress ? 'text-green-600' : 'text-red-600'}`}>
                    {hasSenderAddress ? '✓ Configurée' : '✗ Non configurée'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-orange-900">{t('admin.configuration.organizationGuard.configurationInProgress.contacts')}</span>
                  <span className={`font-medium ${hasContactConfigured ? 'text-green-600' : 'text-red-600'}`}>
                    {hasContactConfigured
                      ? t('admin.configuration.organizationGuard.configurationInProgress.contactsConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.contactsNone')}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm mb-6">
              {t('admin.configuration.organizationGuard.configurationInProgress.completionMessage')}
            </p>

            <div className="space-y-3">
              <button
                onClick={() => globalThis.location.href = '/'}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('admin.configuration.organizationGuard.configurationInProgress.backToHome')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Pour les autres routes (admin), vérifier la configuration complète (sans adresse expéditeur - on doit pouvoir y accéder pour la configurer)
  if (!isOrderRoute && (!isStripeValid || !hasShippingMethods || !hasContactConfigured || !isGeneralReady)) {
    return (
      <div className="fixed inset-0 bg-gray-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-10 h-10 text-yellow-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {t('admin.configuration.organizationGuard.configurationInProgress.title')}
            </h1>
            <p
              className="text-gray-600 mb-2"
              dangerouslySetInnerHTML={{
                __html: t('admin.configuration.organizationGuard.configurationInProgress.message', { name: configuration.name || idorga || '' })
              }}
            />
            <p className="text-gray-500 text-sm mb-4">
              {t('admin.configuration.organizationGuard.configurationInProgress.adminMessage')}
            </p>

            {/* Logo de l'organisation si disponible */}
            {configuration.personalization?.logo && (
              <div className="mb-4">
                <img
                  src={configuration.personalization.logo}
                  alt={configuration.name}
                  className="h-16 mx-auto object-contain"
                />
              </div>
            )}

            {/* Détails du statut de configuration */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-sm font-medium text-yellow-900 mb-2">
                {t('admin.configuration.organizationGuard.configurationInProgress.statusTitle')}
              </h3>
              <div className="space-y-1 text-sm">
                {/* Statut Infos Légales */}
                <div className="flex items-center justify-between">
                  <span className="text-yellow-800">{t('admin.configuration.organizationGuard.configurationInProgress.legalInfo')}</span>
                  <span className={`font-medium ${isGeneralReady ? 'text-green-600' : 'text-red-600'}`}>
                    {isGeneralReady
                      ? t('admin.configuration.organizationGuard.configurationInProgress.legalInfoConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.legalInfoNone')}
                  </span>
                </div>
                {/* Statut Paiements */}
                <div className="flex items-center justify-between">
                  <span className="text-yellow-800">Paiements :</span>
                  <span className={`font-medium ${isStripeValid ? 'text-green-600' : 'text-red-600'}`}>
                    {isStripeValid ? '✓ Configuré' : '✗ Non configuré'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-800">{t('admin.configuration.organizationGuard.configurationInProgress.shippingMethods')}</span>
                  <span className={`font-medium ${hasShippingMethods ? 'text-green-600' : 'text-red-600'}`}>
                    {hasShippingMethods
                      ? t('admin.configuration.organizationGuard.configurationInProgress.shippingMethodsConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.shippingMethodsNone')}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-800">Adresse expéditeur</span>
                  <span className={`font-medium ${hasSenderAddress ? 'text-green-600' : 'text-red-600'}`}>
                    {hasSenderAddress ? '✓ Configurée' : '✗ Non configurée'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-yellow-800">{t('admin.configuration.organizationGuard.configurationInProgress.contacts')}</span>
                  <span className={`font-medium ${hasContactConfigured ? 'text-green-600' : 'text-red-600'}`}>
                    {hasContactConfigured
                      ? t('admin.configuration.organizationGuard.configurationInProgress.contactsConfigured')
                      : t('admin.configuration.organizationGuard.configurationInProgress.contactsNone')}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-gray-500 text-sm mb-6">
              {t('admin.configuration.organizationGuard.configurationInProgress.adminCompletionMessage')}
            </p>

            <div className="space-y-3">
              {isAdmin ? (
                <button
                  onClick={() => globalThis.location.href = `/orga/${idorga}/admin/configuration`}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('admin.configuration.organizationGuard.configurationInProgress.backToDashboard')}
                </button>
              ) : (
                <button
                  onClick={() => globalThis.location.href = `/orga/${idorga}/admin/configuration`}
                  className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('admin.configuration.organizationGuard.configurationInProgress.backToConfiguration')}
                </button>
              )}
              <button
                onClick={() => globalThis.location.href = '/'}
                className="w-full px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                {t('admin.configuration.organizationGuard.configurationInProgress.backToHome')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Tout est OK, afficher le contenu enveloppé du provider de configuration
  return (
    <OrgaConfigurationProvider
      organizationId={idorga}
      initialConfiguration={configuration}
      once={true}
    >
      {children}
    </OrgaConfigurationProvider>
  );
};

export default OrganizationGuard;















